import React from 'react'

import fetchApi, { ContentType, InternalServerError } from '../../utils/fetch_api'
import { getUuid } from '../../utils/helpers'
import { HLC, compareHlc, incrementHlc, parseHlc, serializeHlc } from '../../utils/hlc'
import { getByteLengthOfString } from '../../utils/string-byte-length'
import { AutoSaveElement, DrawingContainerType } from './constants'

const RETRY_LIMIT = 5

export function useDrawingAutoSaveApi() {
  const postRetryCountRef = React.useRef(0)
  const deleteRetryCountRef = React.useRef(0)

  const fetchDrawings = React.useCallback(
    async ({
      tickers,
      containerTypes,
      lastChange,
    }: {
      tickers: string[]
      containerTypes: DrawingContainerType[]
      lastChange: string
    }) => {
      try {
        const data = await fetchApi<{ drawings: Array<Omit<AutoSaveElement, 'lastChange'> & { lastChange: string }> }>({
          location: '/api/auto_save.ashx',
          queryParameters: {
            ticker: tickers.join(','),
            containerTypes: containerTypes.join(',') || undefined,
            lastChange,
          },
          throwOnStatusCodes: [404],
        })
        return data.drawings.map((drawing) => ({ ...drawing, lastChange: parseHlc(drawing.lastChange) }))
      } catch {
        // ignore error
      }

      return []
    },
    []
  )

  const saveDrawings = React.useCallback(async (drawings: AutoSaveElement[]): Promise<HLC | undefined> => {
    try {
      const bodyJsonString = JSON.stringify(
        drawings.map((item) => ({
          ...item,
          elementAttrs: JSON.stringify(item.elementAttrs),
          lastChange: serializeHlc(item.lastChange),
        }))
      )
      let keepalive = false
      try {
        keepalive = getByteLengthOfString(bodyJsonString) < 64000
      } catch (error: any) {
        window.Sentry?.captureException(error)
      }
      await fetchApi({
        location: '/api/auto_save.ashx',
        method: 'POST',
        throwOnStatusCodes: [404, 500],
        contentType: ContentType.ApplicationJson,
        body: bodyJsonString,
        keepalive,
      })
      const latestChangeHlc = [...drawings].sort((a, b) => compareHlc(b.lastChange, a.lastChange))[0]
      postRetryCountRef.current = 0
      return latestChangeHlc.lastChange
    } catch (error) {
      if (error instanceof InternalServerError) {
        if (postRetryCountRef.current < RETRY_LIMIT) {
          postRetryCountRef.current = postRetryCountRef.current + 1
          await new Promise((r) => setTimeout(r, 1000))
          return saveDrawings(drawings)
        } else {
          postRetryCountRef.current = 0
        }
      }
      // ignore error
      window.Sentry?.captureException(error, {
        extra: {
          drawings: drawings.map(({ lastChange, changeType, elementId, containerType }) => ({
            elementId,
            lastChange,
            changeType,
            containerType,
          })),
        },
      })
      return undefined
    }
  }, [])

  const deleteAllDrawings = React.useCallback(
    async ({ tickers, lastLocalChange }: { tickers: string[]; lastLocalChange: HLC }): Promise<void> => {
      try {
        await fetchApi({
          location: `/api/auto_save.ashx?ticker=${tickers.join(',')}&hlc=${serializeHlc({
            ...incrementHlc(lastLocalChange, Date.now()),
            uuid: getUuid(), // we provide new UUID for HLC so we can fetch 'destroy' changes in next GET request
          })}`,
          method: 'DELETE',
          throwOnStatusCodes: [404, 500],
          keepalive: true,
        })
        deleteRetryCountRef.current = 0
      } catch (error) {
        if (error instanceof InternalServerError) {
          if (deleteRetryCountRef.current < RETRY_LIMIT) {
            deleteRetryCountRef.current = deleteRetryCountRef.current + 1
            await new Promise((r) => setTimeout(r, 1000))
            return deleteAllDrawings({ tickers, lastLocalChange })
          } else {
            deleteRetryCountRef.current = 0
          }
        }
        // ignore error
        window.Sentry?.captureException(error)
      }
    },
    []
  )

  return { fetchDrawings, saveDrawings, deleteAllDrawings }
}
